import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { ToastContainer } from '@/components/common';
import { useToast } from '@/lib/hooks/useToast';
import {
  getAgreement, createAgreement, updateAgreement,
  sendAgreementForSigning,
  generateScopeOfWork,
  DEFAULT_CLAUSES, DEFAULT_WO_CLAUSES, DEFAULT_MATERIALS_SUPPLY,
  type Agreement, type AgreementClauses, type AgreementType,
  type PaymentMilestone, type MaterialsSupply,
} from '@/lib/api/agreements';
import { getClients, type Client } from '@/lib/api/clients';
import { getJobs, type Job } from '@/lib/api/jobs';
import { getSettingsByCategory } from '@/lib/api/settings';
import { agreementPath } from '@/lib/utils/navPaths';

const STATUS_OPTIONS = [
  { value: 'draft',     label: 'Draft' },
  { value: 'sent',      label: 'Sent' },
  { value: 'signed',    label: 'Signed' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

const STATUS_COLORS: Record<string, string> = {
  draft:     'text-gray-600 bg-gray-100 border-gray-200',
  sent:      'text-blue-700 bg-blue-50 border-blue-200',
  signed:    'text-green-700 bg-green-50 border-green-200',
  cancelled: 'text-red-700 bg-red-50 border-red-200',
};

const fmt = (d?: string) =>
  d ? new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

const fmtMoney = (v?: number | null) =>
  v == null ? '' : `$${Number(v).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type FormData = Omit<Agreement, 'id' | 'created_at' | 'signed_at' | 'signed_by_name' | 'sent_at' | 'client_display_name' | 'job_title' | 'job_number'>;

const EMPTY_FORM: FormData = {
  agreement_number: '',
  client_id: 0,
  job_id: null,
  title: '',
  status: 'draft',
  agreement_type: 'work_agreement',
  // Client
  client_name: '',
  client_address: '',
  client_email: '',
  client_phone: '',
  // Contractor
  company_name: 'OchrePoint Contracting LTD',
  company_address: '750 Allenby Road, Duncan, BC  V9L 5J6',
  company_email: 'info@ochrepoint.ca',
  company_phone: '(250) 555-0190',
  company_gst: '',
  company_bc_inc: '',
  // Principal contractor
  principal_name: '',
  principal_bc_inc: '',
  principal_gst: '',
  principal_address: '',
  principal_rep: '',
  principal_phone: '',
  principal_email: '',
  // WCB
  wcb_account: '',
  wcb_classification: '',
  wcb_effective_date: '',
  // Insurance
  insurance_insurer: '',
  insurance_policy: '',
  insurance_cgl_limit: '',
  // Site
  site_city: '',
  site_postal_code: '',
  // Scope
  scope_of_work: '',
  scope_exclusions: '',
  // Payment
  contract_value: null,
  deposit_percent: 25,
  payment_schedule: 'on-completion',
  payment_notes: '',
  hourly_rate: null,
  nte_cap: null,
  payment_due_days: 30,
  holdback_percent: 10,
  payment_milestones: null,
  materials_supply: null,
  // Timeline
  start_date: '',
  estimated_end_date: '',
  // Clauses
  clauses: DEFAULT_CLAUSES,
  internal_notes: '',
  agreement_date: new Date().toISOString().split('T')[0],
};

// ── Preview: Standard Work Agreement ────────────────────────────────────────

const WorkAgreementPreview = ({ form, agreementNumber }: { form: FormData; agreementNumber: string }) => {
  const clauses = form.clauses ?? DEFAULT_CLAUSES;
  const depositAmt = form.contract_value && form.deposit_percent
    ? form.contract_value * (form.deposit_percent / 100)
    : null;
  const CLAUSE_HEADINGS: Record<string, string> = {
    change_orders:          'Change Orders',
    warranty:               'Warranty & Deficiencies',
    lien_rights:            'BC Builders Lien Act Compliance',
    confidentiality:        'Confidentiality & Client Privacy',
    termination:            'Termination',
    dispute:                'Dispute Resolution',
    general_conditions:     'General Conditions',
    schedule_time:          'Schedule & Time',
    independent_contractor: 'Independent Contractor Status',
    wcb_ohs:                'WCB & Occupational Health & Safety',
    insurance_requirements: 'Insurance Requirements',
    additional:             'Additional Terms',
  };

  return (
    <div id="agreement-print-preview" className="bg-white text-gray-900 font-['serif'] text-sm leading-relaxed p-8 max-w-2xl mx-auto">
      <div className="border-b-2 border-[#C77D3F] pb-4 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xl font-bold text-[#C77D3F] tracking-wide">OCHREPOINT CONTRACTING LTD</p>
            <p className="text-xs text-gray-500 italic">"Built on Skill, Finished with Pride"</p>
            <p className="text-xs text-gray-600 mt-1">{form.company_address}</p>
            <p className="text-xs text-gray-600">{form.company_phone} · {form.company_email}</p>
            {(form.company_gst || form.company_bc_inc) && (
              <p className="text-xs text-gray-500 mt-0.5">
                {form.company_gst && <>GST No: {form.company_gst}</>}
                {form.company_gst && form.company_bc_inc && ' · '}
                {form.company_bc_inc && <>BC Inc. No: {form.company_bc_inc}</>}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Work Agreement</p>
            <p className="font-mono text-sm font-bold text-gray-900 mt-1">{agreementNumber || '—'}</p>
            <p className="text-xs text-gray-500 mt-0.5">Date: {fmt(form.agreement_date) || '—'}</p>
          </div>
        </div>
      </div>

      {form.title && (
        <h1 className="text-lg font-bold text-gray-900 text-center mb-6 uppercase tracking-wide">{form.title}</h1>
      )}

      <section className="mb-6">
        <h2 className="text-xs font-bold uppercase tracking-widest text-[#C77D3F] border-b border-gray-200 pb-1 mb-3">Parties</h2>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-bold uppercase text-gray-500 mb-1">Contractor</p>
            <p className="font-semibold">{form.company_name}</p>
            {form.company_address && <p className="text-xs text-gray-600 whitespace-pre-wrap">{form.company_address}</p>}
            {form.company_email && <p className="text-xs text-gray-600">{form.company_email}</p>}
            {form.company_phone && <p className="text-xs text-gray-600">{form.company_phone}</p>}
            {form.company_gst && <p className="text-xs text-gray-600">GST No: {form.company_gst}</p>}
            {form.company_bc_inc && <p className="text-xs text-gray-600">BC Inc. No: {form.company_bc_inc}</p>}
            {form.wcb_account && <p className="text-xs text-gray-600 mt-1">WCB: {form.wcb_account}{form.wcb_classification ? ` / ${form.wcb_classification}` : ''}</p>}
            {form.insurance_policy && <p className="text-xs text-gray-600">CGL Policy: {form.insurance_policy}{form.insurance_cgl_limit ? ` (${form.insurance_cgl_limit})` : ''}</p>}
          </div>
          <div>
            <p className="text-xs font-bold uppercase text-gray-500 mb-1">Client</p>
            <p className="font-semibold">{form.client_name || '—'}</p>
            {form.client_address && <p className="text-xs text-gray-600 whitespace-pre-wrap">{form.client_address}</p>}
            {form.client_email && <p className="text-xs text-gray-600">{form.client_email}</p>}
            {form.client_phone && <p className="text-xs text-gray-600">{form.client_phone}</p>}
          </div>
        </div>
      </section>

      {form.scope_of_work && (
        <section className="mb-6">
          <h2 className="text-xs font-bold uppercase tracking-widest text-[#C77D3F] border-b border-gray-200 pb-1 mb-3">Scope of Work</h2>
          <p className="text-xs whitespace-pre-wrap text-gray-800">{form.scope_of_work}</p>
        </section>
      )}

      {form.scope_exclusions && (
        <section className="mb-6">
          <h2 className="text-xs font-bold uppercase tracking-widest text-[#C77D3F] border-b border-gray-200 pb-1 mb-3">Exclusions</h2>
          <p className="text-xs whitespace-pre-wrap text-gray-800">{form.scope_exclusions}</p>
        </section>
      )}

      {(form.contract_value || form.payment_notes) && (
        <section className="mb-6">
          <h2 className="text-xs font-bold uppercase tracking-widest text-[#C77D3F] border-b border-gray-200 pb-1 mb-3">Payment Terms</h2>
          <div className="space-y-1 text-xs text-gray-800">
            {form.contract_value && <p><span className="font-semibold">Contract Value:</span> {fmtMoney(form.contract_value)} (plus applicable GST/PST)</p>}
            {depositAmt && <p><span className="font-semibold">Deposit:</span> {form.deposit_percent}% ({fmtMoney(depositAmt)}) due upon signing</p>}
            {form.payment_notes && <p className="mt-1 whitespace-pre-wrap">{form.payment_notes}</p>}
          </div>
        </section>
      )}

      {(form.start_date || form.estimated_end_date) && (
        <section className="mb-6">
          <h2 className="text-xs font-bold uppercase tracking-widest text-[#C77D3F] border-b border-gray-200 pb-1 mb-3">Project Timeline</h2>
          <div className="text-xs text-gray-800 space-y-1">
            {form.start_date && <p><span className="font-semibold">Start Date:</span> {fmt(form.start_date)}</p>}
            {form.estimated_end_date && <p><span className="font-semibold">Estimated Completion:</span> {fmt(form.estimated_end_date)}</p>}
          </div>
        </section>
      )}

      {(Object.entries(clauses) as [keyof AgreementClauses, { enabled: boolean; content: string }][])
        .filter(([, v]) => v.enabled && v.content)
        .map(([key, v]) => (
          <section key={key} className="mb-6">
            <h2 className="text-xs font-bold uppercase tracking-widest text-[#C77D3F] border-b border-gray-200 pb-1 mb-3">{CLAUSE_HEADINGS[key]}</h2>
            <p className="text-xs text-gray-800 whitespace-pre-wrap">{v.content}</p>
          </section>
        ))}

      <section className="mt-10">
        <h2 className="text-xs font-bold uppercase tracking-widest text-[#C77D3F] border-b border-gray-200 pb-1 mb-4">Acceptance & Signatures</h2>
        <p className="text-xs text-gray-600 mb-6">By signing below, both parties agree to the terms and conditions set out in this Work Agreement.</p>
        <div className="grid grid-cols-2 gap-8">
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-6">For OchrePoint Contracting LTD</p>
            <div className="border-t border-gray-400 pt-1"><p className="text-xs text-gray-500">Authorized Signature</p></div>
            <div className="border-t border-gray-300 pt-1 mt-4"><p className="text-xs text-gray-500">Date</p></div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-6">For {form.client_name || 'Client'}</p>
            <div className="border-t border-gray-400 pt-1"><p className="text-xs text-gray-500">Authorized Signature</p></div>
            <div className="border-t border-gray-300 pt-1 mt-4"><p className="text-xs text-gray-500">Date</p></div>
          </div>
        </div>
      </section>

      <p className="text-center text-xs text-gray-400 border-t border-gray-100 mt-8 pt-4">
        {form.company_name} · {agreementNumber || '—'} · Confidential
      </p>
    </div>
  );
};

// ── Preview: Subcontractor Work Order ────────────────────────────────────────

const SubcontractorWOPreview = ({ form, agreementNumber }: { form: FormData; agreementNumber: string }) => {
  const ms = form.materials_supply ?? DEFAULT_MATERIALS_SUPPLY;
  const milestones = form.payment_milestones ?? [];
  const clauses = form.clauses ?? DEFAULT_WO_CLAUSES;
  const CLAUSE_HEADINGS: Record<string, string> = {
    schedule_time:           '5. Schedule & Time',
    change_orders:           '6. Changes to the Work',
    independent_contractor:  '7. Independent Contractor Status',
    wcb_ohs:                 '8. WCB & Occupational Health & Safety',
    insurance_requirements:  '9. Insurance Requirements',
    warranty:                '10. Warranty & Deficiencies',
    lien_rights:             '11. BC Builders Lien Act Compliance',
    confidentiality:         '12. Confidentiality & Client Privacy',
    termination:             '13. Termination',
    dispute:                 '14. Dispute Resolution',
    general_conditions:      '15. General Conditions',
    additional:              'Additional Terms',
  };

  return (
    <div id="agreement-print-preview" className="bg-white text-gray-900 font-['serif'] text-[11px] leading-relaxed p-8 max-w-2xl mx-auto">
      {/* Header */}
      <div className="border-b-2 border-[#C77D3F] pb-3 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-lg font-bold text-[#C77D3F] tracking-wide">OCHREPOINT CONTRACTING LTD</p>
            <p className="text-[10px] text-gray-500 italic">"Built on Skill, Finished with Pride"</p>
            <p className="text-[10px] text-gray-600 mt-0.5">{form.company_address}</p>
            <p className="text-[10px] text-gray-600">{form.company_phone} · {form.company_email}</p>
            {(form.company_gst || form.company_bc_inc) && (
              <p className="text-[10px] text-gray-500 mt-0.5">
                {form.company_gst && <>GST No: {form.company_gst}</>}
                {form.company_gst && form.company_bc_inc && ' · '}
                {form.company_bc_inc && <>BC Inc. No: {form.company_bc_inc}</>}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Subcontractor Work Order</p>
            <p className="font-mono text-sm font-bold text-gray-900 mt-1">{agreementNumber || '—'}</p>
            <p className="text-[10px] text-gray-500">Date: {fmt(form.agreement_date) || '—'}</p>
          </div>
        </div>
      </div>

      {form.title && (
        <h1 className="text-sm font-bold text-center mb-4 uppercase tracking-wide border border-gray-300 py-2 px-4 rounded">{form.title}</h1>
      )}

      {/* 1. Parties */}
      <section className="mb-4">
        <h2 className="text-[10px] font-bold uppercase tracking-widest text-[#C77D3F] border-b border-gray-200 pb-1 mb-2">1. Parties</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase text-gray-500 mb-1">Principal Contractor</p>
            <p className="font-semibold text-xs">{form.principal_name || '—'}</p>
            {form.principal_bc_inc && <p className="text-[10px] text-gray-600">BC Inc. No: {form.principal_bc_inc}</p>}
            {form.principal_gst && <p className="text-[10px] text-gray-600">GST: {form.principal_gst}</p>}
            {form.principal_address && <p className="text-[10px] text-gray-600 whitespace-pre-wrap">{form.principal_address}</p>}
            {form.principal_rep && <p className="text-[10px] text-gray-600">Attn: {form.principal_rep}</p>}
            {form.principal_phone && <p className="text-[10px] text-gray-600">{form.principal_phone}</p>}
            {form.principal_email && <p className="text-[10px] text-gray-600">{form.principal_email}</p>}
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase text-gray-500 mb-1">Subcontractor</p>
            <p className="font-semibold text-xs">{form.company_name}</p>
            {form.company_bc_inc && <p className="text-[10px] text-gray-600">BC Inc. No: {form.company_bc_inc}</p>}
            {form.company_gst && <p className="text-[10px] text-gray-600">GST No: {form.company_gst}</p>}
            {form.company_address && <p className="text-[10px] text-gray-600 whitespace-pre-wrap">{form.company_address}</p>}
            {form.company_phone && <p className="text-[10px] text-gray-600">{form.company_phone}</p>}
            {form.company_email && <p className="text-[10px] text-gray-600">{form.company_email}</p>}
            {form.wcb_account && <p className="text-[10px] text-gray-600 mt-1">WCB Account: {form.wcb_account}</p>}
            {form.wcb_classification && <p className="text-[10px] text-gray-600">WCB Class: {form.wcb_classification}</p>}
            {form.insurance_insurer && <p className="text-[10px] text-gray-600">Insurer: {form.insurance_insurer}</p>}
            {form.insurance_policy && <p className="text-[10px] text-gray-600">CGL Policy: {form.insurance_policy}</p>}
            {form.insurance_cgl_limit && <p className="text-[10px] text-gray-600">CGL Limit: {form.insurance_cgl_limit}</p>}
          </div>
        </div>
      </section>

      {/* 2. Project Details */}
      <section className="mb-4">
        <h2 className="text-[10px] font-bold uppercase tracking-widest text-[#C77D3F] border-b border-gray-200 pb-1 mb-2">2. Project Details</h2>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
          <p><span className="font-semibold">Client:</span> {form.client_name || '—'}</p>
          <p><span className="font-semibold">Site Address:</span> {form.client_address || '—'}</p>
          {form.site_city && <p><span className="font-semibold">City:</span> {form.site_city}</p>}
          {form.site_postal_code && <p><span className="font-semibold">Postal Code:</span> {form.site_postal_code}</p>}
          {form.start_date && <p><span className="font-semibold">Work Start:</span> {fmt(form.start_date)}</p>}
          {form.estimated_end_date && <p><span className="font-semibold">Substantial Completion:</span> {fmt(form.estimated_end_date)}</p>}
        </div>
      </section>

      {/* 3. Scope of Work */}
      {form.scope_of_work && (
        <section className="mb-4">
          <h2 className="text-[10px] font-bold uppercase tracking-widest text-[#C77D3F] border-b border-gray-200 pb-1 mb-2">3. Scope of Work</h2>
          <p className="text-[10px] text-gray-600 italic mb-2">The Contractor agrees to furnish all labour, supervision, tools, and equipment (unless noted below) required to complete the following work in a good and workmanlike manner, in accordance with the BC Building Code, applicable municipal bylaws, and manufacturer specifications.</p>
          <p className="whitespace-pre-wrap text-gray-800">{form.scope_of_work}</p>
          {form.scope_exclusions && (
            <>
              <p className="text-[10px] font-semibold mt-2 mb-1">3.1 Exclusions</p>
              <p className="whitespace-pre-wrap text-gray-700">{form.scope_exclusions}</p>
            </>
          )}
          {/* 3.2 Materials */}
          {(ms.labour_only || ms.materials_by_principal || ms.materials_by_subcontractor || ms.equipment_by_principal || ms.equipment_by_subcontractor) && (
            <div className="mt-2">
              <p className="text-[10px] font-semibold mb-1">3.2 Materials & Equipment</p>
              <div className="grid grid-cols-2 gap-x-4 text-[10px]">
                {ms.labour_only && <p>☑ Labour only (all materials by Principal)</p>}
                {ms.materials_by_principal && <p>☑ Materials supplied by Principal</p>}
                {ms.materials_by_subcontractor && <p>☑ Materials supplied by Subcontractor</p>}
                {ms.equipment_by_principal && <p>☑ Equipment supplied by Principal</p>}
                {ms.equipment_by_subcontractor && <p>☑ Equipment supplied by Subcontractor</p>}
              </div>
            </div>
          )}
        </section>
      )}

      {/* 4. Contract Price & Payment */}
      <section className="mb-4">
        <h2 className="text-[10px] font-bold uppercase tracking-widest text-[#C77D3F] border-b border-gray-200 pb-1 mb-2">4. Contract Price & Payment</h2>
        {form.contract_value != null && (
          <p className="mb-1"><span className="font-semibold">Fixed Contract Price:</span> {fmtMoney(form.contract_value)} (plus GST)</p>
        )}
        {form.hourly_rate != null && (
          <p className="mb-1"><span className="font-semibold">T&M Hourly Rate:</span> {fmtMoney(form.hourly_rate)}/hr
            {form.nte_cap != null && ` — NTE: ${fmtMoney(form.nte_cap)}`}
          </p>
        )}
        {form.payment_due_days > 0 && (
          <p className="mb-1"><span className="font-semibold">Payment Due:</span> {form.payment_due_days} days from invoice</p>
        )}
        {form.holdback_percent > 0 && (
          <p className="mb-1"><span className="font-semibold">Holdback:</span> {form.holdback_percent}% per the BC Builders Lien Act</p>
        )}
        {milestones.length > 0 && (
          <table className="w-full mt-2 border border-gray-200 text-[10px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-2 py-1 border-b border-gray-200">Milestone</th>
                <th className="text-right px-2 py-1 border-b border-gray-200">Amount</th>
                <th className="text-right px-2 py-1 border-b border-gray-200">%</th>
                <th className="text-left px-2 py-1 border-b border-gray-200">Due When</th>
              </tr>
            </thead>
            <tbody>
              {milestones.map((m, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="px-2 py-1">{m.description}</td>
                  <td className="px-2 py-1 text-right">{m.amount != null ? fmtMoney(m.amount) : '—'}</td>
                  <td className="px-2 py-1 text-right">{m.percent != null ? `${m.percent}%` : '—'}</td>
                  <td className="px-2 py-1">{m.due_when}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {form.payment_notes && <p className="mt-1 whitespace-pre-wrap text-gray-700">{form.payment_notes}</p>}
      </section>

      {/* Standard clauses */}
      {(Object.entries(clauses) as [keyof AgreementClauses, { enabled: boolean; content: string }][])
        .filter(([, v]) => v.enabled && v.content)
        .map(([key, v]) => (
          <section key={key} className="mb-3">
            <h2 className="text-[10px] font-bold uppercase tracking-widest text-[#C77D3F] border-b border-gray-200 pb-1 mb-1">{CLAUSE_HEADINGS[key]}</h2>
            <p className="whitespace-pre-wrap text-gray-800">{v.content}</p>
          </section>
        ))}

      {/* Signatures */}
      <section className="mt-8 border-t border-gray-300 pt-4">
        <h2 className="text-[10px] font-bold uppercase tracking-widest text-[#C77D3F] mb-3">Execution</h2>
        <div className="grid grid-cols-2 gap-8">
          <div>
            <p className="text-[10px] font-semibold mb-1">PRINCIPAL CONTRACTOR</p>
            <p className="text-[10px] text-gray-600 mb-6">{form.principal_name || '—'}</p>
            <div className="border-t border-gray-400 pt-1 mb-3"><p className="text-[10px] text-gray-500">Authorized Signature</p></div>
            <div className="border-t border-gray-300 pt-1 mb-3"><p className="text-[10px] text-gray-500">Print Name & Title</p></div>
            <div className="border-t border-gray-300 pt-1"><p className="text-[10px] text-gray-500">Date</p></div>
          </div>
          <div>
            <p className="text-[10px] font-semibold mb-1">SUBCONTRACTOR</p>
            <p className="text-[10px] text-gray-600 mb-6">{form.company_name}</p>
            <div className="border-t border-gray-400 pt-1 mb-3"><p className="text-[10px] text-gray-500">Authorized Signature</p></div>
            <div className="border-t border-gray-300 pt-1 mb-3"><p className="text-[10px] text-gray-500">Print Name & Title</p></div>
            <div className="border-t border-gray-300 pt-1"><p className="text-[10px] text-gray-500">Date</p></div>
          </div>
        </div>
      </section>

      <p className="text-center text-[10px] text-gray-400 border-t border-gray-100 mt-6 pt-3">
        {form.company_name} · {agreementNumber || '—'} · Confidential
      </p>
    </div>
  );
};

const AgreementPreview = ({ form, agreementNumber }: { form: FormData; agreementNumber: string }) => {
  if (form.agreement_type === 'subcontractor_work_order') {
    return <SubcontractorWOPreview form={form} agreementNumber={agreementNumber} />;
  }
  return <WorkAgreementPreview form={form} agreementNumber={agreementNumber} />;
};

// ── Form section wrapper ──────────────────────────────────────────────────────

const Section = ({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
    <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
      <span className="material-symbols-outlined text-primary text-base">{icon}</span>
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
    </div>
    <div className="p-4 space-y-3">{children}</div>
  </div>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

const Toggle = ({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none">
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-1'}`} />
    </button>
    <span className="text-xs font-semibold text-gray-700">{label}</span>
  </label>
);

const inputCls = "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none";
const textareaCls = `${inputCls} resize-y min-h-[80px]`;

// ── Main page ────────────────────────────────────────────────────────────────

export const AgreementBuilderPage = () => {
  const { id, clientId } = useParams<{ id?: string; clientId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toasts, removeToast, toast } = useToast();
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [agreementNumber, setAgreementNumber] = useState('');
  const [loadedId, setLoadedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [tab, setTab] = useState<'form' | 'preview'>('form');
  const [sending, setSending] = useState(false);
  const [signLink, setSignLink] = useState<string | null>(null);
  const [signEmailSent, setSignEmailSent] = useState<string | null>(null);
  const isNew = !id || id === 'new';
  const printRef = useRef<HTMLDivElement>(null);
  const isWO = form.agreement_type === 'subcontractor_work_order';

  useEffect(() => {
    getClients().then(setClients).catch(() => {});
    getJobs().then(r => setJobs(r.jobs || [])).catch(() => {});
  }, []);

  // Upgrade the flat /agreements/:id URL to the canonical /clients/:c/agreements/:id once
  // the agreement (and thus its client) is known, so the address bar mirrors the hierarchy.
  useEffect(() => {
    if (isNew || !form.client_id) return;
    if (String(clientId) !== String(form.client_id)) {
      navigate(agreementPath(form.client_id, id!), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.client_id, clientId, id]);

  // Populate real company info + clause templates from settings for new agreements
  useEffect(() => {
    if (!isNew) return;
    Promise.all([
      getSettingsByCategory('company').catch(() => ({})),
      getSettingsByCategory('agreements').catch(() => ({})),
    ]).then(([s, ag]: [Record<string, string>, Record<string, string>]) => {
      const addressLines = [
        s.address,
        [s.city, s.state, s.zipCode].filter(Boolean).join(', '),
      ].filter(Boolean);

      setForm(p => {
        const isWOForm = p.agreement_type === 'subcontractor_work_order';
        const base = isWOForm ? DEFAULT_WO_CLAUSES : DEFAULT_CLAUSES;
        const clauses: AgreementClauses = {
          change_orders:          { enabled: base.change_orders.enabled,          content: ag.clause_change_orders          || base.change_orders.content },
          warranty:               { enabled: base.warranty.enabled,               content: ag.clause_warranty               || base.warranty.content },
          lien_rights:            { enabled: base.lien_rights.enabled,            content: ag.clause_lien_rights            || base.lien_rights.content },
          confidentiality:        { enabled: base.confidentiality.enabled,        content: ag.clause_confidentiality        || base.confidentiality.content },
          termination:            { enabled: base.termination.enabled,            content: ag.clause_termination            || base.termination.content },
          dispute:                { enabled: base.dispute.enabled,                content: ag.clause_dispute                || base.dispute.content },
          general_conditions:     { enabled: base.general_conditions.enabled,     content: ag.clause_general_conditions     || base.general_conditions.content },
          schedule_time:          { enabled: base.schedule_time.enabled,          content: ag.clause_schedule_time          || base.schedule_time.content },
          independent_contractor: { enabled: base.independent_contractor.enabled, content: ag.clause_independent_contractor || base.independent_contractor.content },
          wcb_ohs:                { enabled: base.wcb_ohs.enabled,                content: ag.clause_wcb_ohs                || base.wcb_ohs.content },
          insurance_requirements: { enabled: base.insurance_requirements.enabled, content: ag.clause_insurance_requirements || base.insurance_requirements.content },
          additional:             { enabled: false, content: '' },
        };
        return {
          ...p,
          company_name:        s.legalName    || s.companyName      || p.company_name,
          company_address:     addressLines.join('\n')               || p.company_address,
          company_email:       s.email        || s.companyEmail      || p.company_email,
          company_phone:       s.phone        || s.fax               || p.company_phone,
          company_gst:         s.taxId        || s.gstNumber         || p.company_gst,
          company_bc_inc:      s.licenseNumber                       || p.company_bc_inc,
          insurance_insurer:   s.insuranceProvider                   || p.insurance_insurer,
          insurance_policy:    s.insurancePolicyNumber               || p.insurance_policy,
          insurance_cgl_limit: s.insuranceCglLimit                   || p.insurance_cgl_limit,
          wcb_account:         s.wcbAccount                          || p.wcb_account,
          wcb_classification:  s.wcbClassification                   || p.wcb_classification,
          wcb_effective_date:  s.wcbEffectiveDate                    || p.wcb_effective_date,
          clauses,
        };
      });
    });
  }, []);

  useEffect(() => {
    if (!isNew) return;
    const clientId = searchParams.get('client_id');
    if (!clientId) return;
    setForm(p => ({
      ...p,
      client_id: parseInt(clientId) || 0,
      job_id: searchParams.get('job_id') ? parseInt(searchParams.get('job_id')!) : null,
      title: searchParams.get('title') || p.title,
      contract_value: searchParams.get('value') ? parseFloat(searchParams.get('value')!) : null,
      client_name: searchParams.get('client_name') || p.client_name,
      client_address: searchParams.get('client_address') || p.client_address,
      client_email: searchParams.get('client_email') || p.client_email,
      client_phone: searchParams.get('client_phone') || p.client_phone,
    }));
  }, []);

  useEffect(() => {
    if (isNew) return;
    getAgreement(id!).then(data => {
      setLoadedId(data.id);
      setAgreementNumber(data.agreement_number);
      setForm({
        agreement_number: data.agreement_number,
        client_id: data.client_id,
        job_id: data.job_id,
        title: data.title,
        status: data.status,
        agreement_type: data.agreement_type || 'work_agreement',
        client_name: data.client_name,
        client_address: data.client_address || '',
        client_email: data.client_email || '',
        client_phone: data.client_phone || '',
        company_name: data.company_name,
        company_address: data.company_address || '',
        company_email: data.company_email || '',
        company_phone: data.company_phone || '',
        company_gst: data.company_gst || '',
        company_bc_inc: data.company_bc_inc || '',
        principal_name: data.principal_name || '',
        principal_bc_inc: data.principal_bc_inc || '',
        principal_gst: data.principal_gst || '',
        principal_address: data.principal_address || '',
        principal_rep: data.principal_rep || '',
        principal_phone: data.principal_phone || '',
        principal_email: data.principal_email || '',
        wcb_account: data.wcb_account || '',
        wcb_classification: data.wcb_classification || '',
        wcb_effective_date: (data.wcb_effective_date || '').split('T')[0],
        insurance_insurer: data.insurance_insurer || '',
        insurance_policy: data.insurance_policy || '',
        insurance_cgl_limit: data.insurance_cgl_limit || '',
        site_city: data.site_city || '',
        site_postal_code: data.site_postal_code || '',
        scope_of_work: data.scope_of_work || '',
        scope_exclusions: data.scope_exclusions || '',
        contract_value: data.contract_value,
        deposit_percent: data.deposit_percent ?? 25,
        payment_schedule: data.payment_schedule || 'on-completion',
        payment_notes: data.payment_notes || '',
        hourly_rate: data.hourly_rate ?? null,
        nte_cap: data.nte_cap ?? null,
        payment_due_days: data.payment_due_days ?? 30,
        holdback_percent: data.holdback_percent ?? 10,
        payment_milestones: data.payment_milestones ?? null,
        materials_supply: data.materials_supply ?? null,
        start_date: (data.start_date || '').split('T')[0],
        estimated_end_date: (data.estimated_end_date || '').split('T')[0],
        clauses: data.clauses ?? DEFAULT_CLAUSES,
        internal_notes: data.internal_notes || '',
        agreement_date: (data.agreement_date || '').split('T')[0],
      });
    }).catch(() => toast.error('Failed to load agreement'));
  }, [id]);

  const set = <K extends keyof FormData>(key: K, value: FormData[K]) =>
    setForm(p => ({ ...p, [key]: value }));

  const onClientChange = (clientId: string) => {
    const cid = parseInt(clientId);
    const c = clients.find(c => Number(c.id) === cid);
    setForm(p => {
      const isWO = p.agreement_type === 'subcontractor_work_order';
      return {
        ...p,
        client_id: cid,
        job_id: null,
        client_name:    c?.name    || '',
        client_address: c?.address || '',
        client_email:   c?.email   || '',
        client_phone:   c?.phone   || '',
        // For WOs the client IS the principal contractor — pre-fill those fields
        ...(isWO && c ? {
          principal_name:    c.company || c.name || p.principal_name,
          principal_address: c.address || p.principal_address,
          principal_email:   c.email   || p.principal_email,
          principal_phone:   c.phone   || p.principal_phone,
        } : {}),
      };
    });
  };

  const clientJobs = form.client_id
    ? jobs.filter(j => j.clientId === form.client_id)
    : [];

  const setClause = (key: keyof AgreementClauses, field: 'enabled' | 'content', value: boolean | string) => {
    setForm(p => ({
      ...p,
      clauses: {
        ...(p.clauses ?? DEFAULT_CLAUSES),
        [key]: { ...(p.clauses ?? DEFAULT_CLAUSES)[key], [field]: value },
      },
    }));
  };

  const setMaterials = (key: keyof MaterialsSupply, value: boolean) => {
    setForm(p => ({
      ...p,
      materials_supply: { ...(p.materials_supply ?? DEFAULT_MATERIALS_SUPPLY), [key]: value },
    }));
  };

  const addMilestone = () => {
    const blank: PaymentMilestone = { description: '', amount: null, percent: null, due_when: '' };
    setForm(p => ({ ...p, payment_milestones: [...(p.payment_milestones ?? []), blank] }));
  };

  const updateMilestone = (i: number, field: keyof PaymentMilestone, value: string | number | null) => {
    setForm(p => {
      const ms = [...(p.payment_milestones ?? [])];
      ms[i] = { ...ms[i], [field]: value };
      return { ...p, payment_milestones: ms };
    });
  };

  const removeMilestone = (i: number) => {
    setForm(p => ({ ...p, payment_milestones: (p.payment_milestones ?? []).filter((_, idx) => idx !== i) }));
  };

  const handleAiScope = async () => {
    if (!form.title) { toast.error('Enter a title first'); return; }
    setAiLoading(true);
    try {
      const text = await generateScopeOfWork({
        title: form.title,
        client_name: form.principal_name || form.client_name || undefined,
        site_address: form.client_address || undefined,
        agreement_type: form.agreement_type,
        notes: form.scope_of_work || undefined,
      });
      set('scope_of_work', text);
      toast.success('Scope generated');
    } catch {
      toast.error('AI generation failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.client_id || !form.title) {
      toast.error('Client and title are required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const result = await createAgreement(form);
        setAgreementNumber(result.agreement_number);
        setLoadedId(result.id);
        navigate(agreementPath(form.client_id, result.id), { replace: true });
        toast.success('Agreement created');
      } else {
        await updateAgreement(loadedId!, form);
        toast.success('Saved');
      }
    } catch {
      toast.error('Failed to save agreement');
    } finally {
      setSaving(false);
    }
  };

  const handleSendForSigning = async () => {
    if (!loadedId) {
      toast.error('Save the agreement first before sending for signature');
      return;
    }
    if (form.status === 'signed') {
      toast.error('This agreement is already signed');
      return;
    }
    setSending(true);
    try {
      const result = await sendAgreementForSigning(loadedId);
      setSignLink(result.signingUrl);
      setSignEmailSent(result.emailSent ? result.clientEmail : null);
      setForm(f => ({ ...f, status: 'sent' }));
      toast.success(result.emailSent ? `Signing link sent to ${result.clientEmail}` : 'Signing link generated');
    } catch {
      toast.error('Failed to generate signing link');
    } finally {
      setSending(false);
    }
  };

  const handlePrint = () => {
    const el = document.getElementById('agreement-print-preview');
    if (!el) return;
    const w = window.open('', '_blank');
    if (!w) return;
    const styles = Array.from(document.styleSheets)
      .map(s => { try { return Array.from(s.cssRules).map(r => r.cssText).join('\n'); } catch { return ''; } })
      .join('\n');
    w.document.write(`<html><head><title>${agreementNumber || 'Agreement'}</title><style>${styles}@media print{body{margin:0}}</style></head><body>${el.outerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 400);
  };

  const clauses = form.clauses ?? DEFAULT_CLAUSES;
  const ms = form.materials_supply ?? DEFAULT_MATERIALS_SUPPLY;
  const milestones = form.payment_milestones ?? [];

  const CLAUSE_LABELS: Record<string, string> = {
    change_orders:          'Change Orders',
    warranty:               'Warranty & Deficiencies',
    lien_rights:            'BC Builders Lien Act Compliance',
    confidentiality:        'Confidentiality & Client Privacy',
    termination:            'Termination',
    dispute:                'Dispute Resolution',
    general_conditions:     'General Conditions',
    schedule_time:          'Schedule & Time (WO)',
    independent_contractor: 'Independent Contractor Status (WO)',
    wcb_ohs:                'WCB & Occupational Health & Safety (WO)',
    insurance_requirements: 'Insurance Requirements (WO)',
    additional:             'Additional Terms',
  };

  const statusCfg = STATUS_COLORS[form.status] || STATUS_COLORS.draft;

  return (
    <MainLayout>
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {form.client_id ? (
        <div className="px-6 pt-4">
        </div>
      ) : null}

      {/* Header */}
      <div className="sticky top-14 md:top-0 z-20 border-b border-gray-200 bg-white px-6 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/agreements')} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
            <span className="material-symbols-outlined text-base">arrow_back</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-primary text-xl">handshake</span>
            <div>
              <p className="text-xs text-gray-500 font-mono">{agreementNumber || 'New Agreement'}</p>
              <p className="text-sm font-bold text-gray-900 leading-tight">{form.title || 'Untitled Agreement'}</p>
            </div>
          </div>
          <span className={`text-xs px-2.5 py-0.5 rounded-full border font-medium ml-2 ${statusCfg}`}>
            {STATUS_OPTIONS.find(o => o.value === form.status)?.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex lg:hidden border border-gray-200 rounded-lg p-0.5 bg-gray-50">
            <button onClick={() => setTab('form')} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${tab === 'form' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>Form</button>
            <button onClick={() => setTab('preview')} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${tab === 'preview' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>Preview</button>
          </div>
          <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
            <span className="material-symbols-outlined text-base">print</span>
            Print
          </button>
          {!isNew && form.status !== 'signed' && form.status !== 'cancelled' && (
            <button
              onClick={handleSendForSigning}
              disabled={sending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors disabled:opacity-60"
            >
              <span className="material-symbols-outlined text-base">{sending ? 'sync' : 'send'}</span>
              {sending ? 'Sending…' : form.status === 'sent' ? 'Resend Link' : 'Send for Signing'}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-base">{saving ? 'sync' : 'save'}</span>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Signing link modal */}
      {signLink && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <span className="material-symbols-outlined text-green-500 text-2xl">
                {signEmailSent ? 'mark_email_read' : 'send'}
              </span>
              <h3 className="font-bold text-gray-900">
                {signEmailSent ? 'Link Sent' : 'Signing Link Ready'}
              </h3>
            </div>
            {signEmailSent ? (
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 mb-3 flex items-center gap-2">
                <span className="material-symbols-outlined text-green-500 text-base">check_circle</span>
                <p className="text-sm text-green-700">Email sent to <strong>{signEmailSent}</strong></p>
              </div>
            ) : (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 mb-3">
                No client email on file — share this link manually.
              </p>
            )}
            <p className="text-xs text-gray-400 mb-3">The signing link expires in 90 days.</p>
            <div className="flex gap-2">
              <input
                readOnly
                value={signLink}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono text-gray-700 bg-gray-50 truncate"
              />
              <button
                onClick={() => { navigator.clipboard.writeText(signLink); toast.success('Copied!'); }}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 text-gray-600 transition-colors flex-shrink-0"
              >
                <span className="material-symbols-outlined text-base">content_copy</span>
              </button>
            </div>
            <button
              onClick={() => setSignLink(null)}
              className="mt-4 w-full py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Split pane */}
      <div className="flex h-[calc(100vh-73px)]">
        {/* Form pane */}
        <div className={`${tab === 'preview' ? 'hidden' : 'flex'} lg:flex flex-col w-full lg:w-[55%] overflow-y-auto border-r border-gray-200 bg-gray-50`}>
          <div className="p-5">
            {isNew && searchParams.get('from_quote') && (
              <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800">
                <span className="material-symbols-outlined text-green-600 text-base">check_circle</span>
                <span>Pre-filled from approved quote <strong>#{searchParams.get('from_quote')}</strong>. Review and save to create the agreement.</span>
              </div>
            )}

            {/* Agreement Details */}
            <Section title="Agreement Details" icon="article">
              <Field label="Agreement Type">
                <select
                  value={form.agreement_type}
                  onChange={e => {
                    const t = e.target.value as AgreementType;
                    setForm(p => {
                      const c = clients.find(cl => Number(cl.id) === p.client_id);
                      return {
                        ...p,
                        agreement_type: t,
                        clauses: t === 'subcontractor_work_order' ? DEFAULT_WO_CLAUSES : DEFAULT_CLAUSES,
                        // Pre-fill principal from selected client when switching to WO
                        ...(t === 'subcontractor_work_order' && c ? {
                          principal_name:    p.principal_name    || c.company || c.name,
                          principal_address: p.principal_address || c.address || '',
                          principal_email:   p.principal_email   || c.email   || '',
                          principal_phone:   p.principal_phone   || c.phone   || '',
                        } : {}),
                      };
                    });
                  }}
                  className={inputCls}
                >
                  <option value="work_agreement">Work Agreement (Client Contract)</option>
                  <option value="subcontractor_work_order">Subcontractor Work Order (OchrePoint as Sub)</option>
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Agreement Date">
                  <input type="date" value={form.agreement_date} onChange={e => set('agreement_date', e.target.value)} className={inputCls} />
                </Field>
                <Field label="Status">
                  <select value={form.status} onChange={e => set('status', e.target.value as FormData['status'])} className={inputCls}>
                    {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Agreement Title *">
                <input
                  type="text" value={form.title}
                  onChange={e => set('title', e.target.value)}
                  placeholder={isWO ? 'e.g. Framing Work — 123 Main St' : 'e.g. Basement Renovation Work Agreement'}
                  className={inputCls}
                />
              </Field>
            </Section>

            {/* Client & Job */}
            <Section title="Client & Job" icon="groups">
              <Field label="Client *">
                <select value={form.client_id || ''} onChange={e => onClientChange(e.target.value)} className={inputCls}>
                  <option value="">Select client…</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>)}
                </select>
              </Field>
              <Field label="Linked Job (optional)">
                <select
                  value={form.job_id ?? ''}
                  onChange={e => set('job_id', e.target.value ? parseInt(e.target.value) : null)}
                  disabled={!form.client_id}
                  className={inputCls}
                >
                  <option value="">{form.client_id ? 'None' : 'Select a client first…'}</option>
                  {clientJobs.map(j => <option key={j.id} value={j.id}>{j.jobNumber} — {j.title}</option>)}
                  {form.client_id && clientJobs.length === 0 && (
                    <option disabled>No jobs for this client</option>
                  )}
                </select>
              </Field>
            </Section>

            {/* Principal Contractor — only for WO */}
            {isWO && (
              <Section title="Principal Contractor" icon="domain">
                <p className="text-xs text-gray-500">The general contractor or developer hiring OchrePoint as subcontractor.</p>
                <Field label="Company Name">
                  <input type="text" value={form.principal_name} onChange={e => set('principal_name', e.target.value)} placeholder="Principal contractor name" className={inputCls} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="BC Inc. No. (optional)">
                    <input type="text" value={form.principal_bc_inc} onChange={e => set('principal_bc_inc', e.target.value)} placeholder="BC-XXXXXXX" className={inputCls} />
                  </Field>
                  <Field label="GST Number (optional)">
                    <input type="text" value={form.principal_gst} onChange={e => set('principal_gst', e.target.value)} placeholder="XXXXXXXXX RT 0001" className={inputCls} />
                  </Field>
                </div>
                <Field label="Address">
                  <textarea value={form.principal_address} onChange={e => set('principal_address', e.target.value)} rows={2} className={textareaCls} />
                </Field>
                <Field label="Contact / Representative">
                  <input type="text" value={form.principal_rep} onChange={e => set('principal_rep', e.target.value)} placeholder="Name and title" className={inputCls} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Phone">
                    <input type="tel" value={form.principal_phone} onChange={e => set('principal_phone', e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Email">
                    <input type="email" value={form.principal_email} onChange={e => set('principal_email', e.target.value)} className={inputCls} />
                  </Field>
                </div>
              </Section>
            )}

            {/* Client info — label differs by type */}
            <Section title={isWO ? 'Project / Site Owner' : 'Client Information'} icon="person">
              <Field label={isWO ? 'Site Owner / Project Owner Name' : 'Full Name / Company'}>
                <input type="text" value={form.client_name} onChange={e => set('client_name', e.target.value)} placeholder={isWO ? 'Property or project owner' : 'Client or company name'} className={inputCls} />
              </Field>
              <Field label="Site Address">
                <textarea value={form.client_address} onChange={e => set('client_address', e.target.value)} placeholder="Street address" className={textareaCls} rows={2} />
              </Field>
              {isWO && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="City">
                    <input type="text" value={form.site_city} onChange={e => set('site_city', e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Postal Code">
                    <input type="text" value={form.site_postal_code} onChange={e => set('site_postal_code', e.target.value)} placeholder="V0X 0X0" className={inputCls} />
                  </Field>
                </div>
              )}
              {!isWO && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Email">
                    <input type="email" value={form.client_email} onChange={e => set('client_email', e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Phone">
                    <input type="tel" value={form.client_phone} onChange={e => set('client_phone', e.target.value)} className={inputCls} />
                  </Field>
                </div>
              )}
            </Section>

            {/* WCB & Insurance */}
            <Section title="WCB & Insurance" icon="security">
              <div className="grid grid-cols-2 gap-3">
                <Field label="WCB Account No.">
                  <input type="text" value={form.wcb_account} onChange={e => set('wcb_account', e.target.value)} placeholder="e.g. 123456" className={inputCls} />
                </Field>
                <Field label="WCB Classification">
                  <input type="text" value={form.wcb_classification} onChange={e => set('wcb_classification', e.target.value)} placeholder="e.g. 712001" className={inputCls} />
                </Field>
              </div>
              <Field label="WCB Coverage Effective Date">
                <input type="date" value={form.wcb_effective_date} onChange={e => set('wcb_effective_date', e.target.value)} className={inputCls} />
              </Field>
              <Field label="CGL Insurer">
                <input type="text" value={form.insurance_insurer} onChange={e => set('insurance_insurer', e.target.value)} placeholder="Insurance company name" className={inputCls} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Policy Number">
                  <input type="text" value={form.insurance_policy} onChange={e => set('insurance_policy', e.target.value)} className={inputCls} />
                </Field>
                <Field label="CGL Limit">
                  <input type="text" value={form.insurance_cgl_limit} onChange={e => set('insurance_cgl_limit', e.target.value)} placeholder="e.g. $2,000,000" className={inputCls} />
                </Field>
              </div>
            </Section>

            {/* Contractor info */}
            <Section title="OchrePoint (Contractor/Sub) Information" icon="business">
              <Field label="Company Name">
                <input type="text" value={form.company_name} onChange={e => set('company_name', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Address">
                <textarea value={form.company_address} onChange={e => set('company_address', e.target.value)} className={textareaCls} rows={2} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email">
                  <input type="email" value={form.company_email} onChange={e => set('company_email', e.target.value)} className={inputCls} />
                </Field>
                <Field label="Phone">
                  <input type="tel" value={form.company_phone} onChange={e => set('company_phone', e.target.value)} className={inputCls} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="GST Number">
                  <input type="text" value={form.company_gst} onChange={e => set('company_gst', e.target.value)} placeholder="e.g. 123456789 RT0001" className={inputCls} />
                </Field>
                <Field label="BC Inc. / Corporate Number">
                  <input type="text" value={form.company_bc_inc} onChange={e => set('company_bc_inc', e.target.value)} placeholder="e.g. BC1234567" className={inputCls} />
                </Field>
              </div>
            </Section>

            {/* Timeline */}
            <Section title="Project Timeline" icon="calendar_month">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Work Start Date">
                  <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className={inputCls} />
                </Field>
                <Field label="Substantial Completion">
                  <input type="date" value={form.estimated_end_date} onChange={e => set('estimated_end_date', e.target.value)} className={inputCls} />
                </Field>
              </div>
            </Section>

            {/* Scope of Work */}
            <Section title="Scope of Work" icon="format_list_bulleted">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-600">Description of work to be performed</label>
                  <button
                    type="button"
                    onClick={handleAiScope}
                    disabled={aiLoading}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-violet-50 border border-violet-200 text-violet-700 text-xs font-medium hover:bg-violet-100 transition-colors disabled:opacity-60"
                    title="Generate scope with AI"
                  >
                    <span className="material-symbols-outlined text-sm">{aiLoading ? 'sync' : 'auto_awesome'}</span>
                    {aiLoading ? 'Generating…' : 'AI Complete'}
                  </button>
                </div>
                <textarea
                  value={form.scope_of_work}
                  onChange={e => set('scope_of_work', e.target.value)}
                  placeholder="Describe all work included in this agreement…"
                  className={textareaCls}
                  rows={6}
                />
              </div>
              <Field label="Exclusions (what is NOT included)">
                <textarea
                  value={form.scope_exclusions}
                  onChange={e => set('scope_exclusions', e.target.value)}
                  placeholder="List anything explicitly excluded…"
                  className={textareaCls}
                  rows={3}
                />
              </Field>

              {/* Materials checkboxes — only for WO */}
              {isWO && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-2">Materials & Equipment Supply</p>
                  <div className="space-y-1.5">
                    {([
                      ['labour_only', 'Labour only — all materials supplied by Principal'],
                      ['materials_by_principal', 'Materials supplied by Principal Contractor'],
                      ['materials_by_subcontractor', 'Materials supplied by Subcontractor (OchrePoint)'],
                      ['equipment_by_principal', 'Equipment supplied by Principal Contractor'],
                      ['equipment_by_subcontractor', 'Equipment supplied by Subcontractor (OchrePoint)'],
                    ] as [keyof MaterialsSupply, string][]).map(([key, label]) => (
                      <Toggle key={key} checked={ms[key]} onChange={v => setMaterials(key, v)} label={label} />
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* Payment Terms */}
            <Section title="Payment Terms" icon="payments">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Fixed Contract Value ($)">
                  <input
                    type="number" min="0" step="0.01"
                    value={form.contract_value ?? ''}
                    onChange={e => set('contract_value', e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="0.00"
                    className={inputCls}
                  />
                </Field>
                {!isWO && (
                  <Field label="Deposit (%)">
                    <input
                      type="number" min="0" max="100" step="1"
                      value={form.deposit_percent}
                      onChange={e => set('deposit_percent', parseFloat(e.target.value) || 0)}
                      className={inputCls}
                    />
                  </Field>
                )}
              </div>

              {isWO && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="T&M Hourly Rate ($/hr)">
                      <input
                        type="number" min="0" step="0.01"
                        value={form.hourly_rate ?? ''}
                        onChange={e => set('hourly_rate', e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="0.00"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Not-To-Exceed Cap ($)">
                      <input
                        type="number" min="0" step="0.01"
                        value={form.nte_cap ?? ''}
                        onChange={e => set('nte_cap', e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="0.00"
                        className={inputCls}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Payment Due (days from invoice)">
                      <input
                        type="number" min="0" step="1"
                        value={form.payment_due_days}
                        onChange={e => set('payment_due_days', parseInt(e.target.value) || 0)}
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Holdback (%)">
                      <input
                        type="number" min="0" max="100" step="0.5"
                        value={form.holdback_percent}
                        onChange={e => set('holdback_percent', parseFloat(e.target.value) || 0)}
                        className={inputCls}
                      />
                    </Field>
                  </div>

                  {/* Payment milestones */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-600">Payment Milestones</p>
                      <button
                        type="button"
                        onClick={addMilestone}
                        className="flex items-center gap-1 text-xs text-primary hover:underline font-medium"
                      >
                        <span className="material-symbols-outlined text-sm">add</span>
                        Add milestone
                      </button>
                    </div>
                    {milestones.length > 0 ? (
                      <div className="space-y-2">
                        {milestones.map((m, i) => (
                          <div key={i} className="grid grid-cols-[1fr_90px_70px_1fr_28px] gap-1.5 items-start">
                            <input
                              type="text" placeholder="Milestone description"
                              value={m.description}
                              onChange={e => updateMilestone(i, 'description', e.target.value)}
                              className={`${inputCls} text-xs`}
                            />
                            <input
                              type="number" placeholder="Amount" min="0" step="0.01"
                              value={m.amount ?? ''}
                              onChange={e => updateMilestone(i, 'amount', e.target.value ? parseFloat(e.target.value) : null)}
                              className={`${inputCls} text-xs`}
                            />
                            <input
                              type="number" placeholder="%" min="0" max="100"
                              value={m.percent ?? ''}
                              onChange={e => updateMilestone(i, 'percent', e.target.value ? parseFloat(e.target.value) : null)}
                              className={`${inputCls} text-xs`}
                            />
                            <input
                              type="text" placeholder="Due when"
                              value={m.due_when}
                              onChange={e => updateMilestone(i, 'due_when', e.target.value)}
                              className={`${inputCls} text-xs`}
                            />
                            <button
                              type="button"
                              onClick={() => removeMilestone(i)}
                              className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            >
                              <span className="material-symbols-outlined text-sm">close</span>
                            </button>
                          </div>
                        ))}
                        <p className="text-[10px] text-gray-400">Columns: Description · Amount ($) · % · Due When</p>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">No milestones added. Click "Add milestone" or use fixed/T&M fields above.</p>
                    )}
                  </div>
                </>
              )}

              {!isWO && (
                <Field label="Payment Schedule">
                  <select value={form.payment_schedule} onChange={e => set('payment_schedule', e.target.value as FormData['payment_schedule'])} className={inputCls}>
                    <option value="on-completion">On Completion</option>
                    <option value="milestone">Milestone-Based</option>
                    <option value="progress">Progress Billing</option>
                    <option value="time_and_materials">Time & Materials</option>
                  </select>
                </Field>
              )}

              <Field label="Payment Notes (optional)">
                <textarea
                  value={form.payment_notes}
                  onChange={e => set('payment_notes', e.target.value)}
                  placeholder="e.g. Invoices submitted bi-weekly…"
                  className={textareaCls}
                  rows={2}
                />
              </Field>
            </Section>

            {/* Standard Clauses */}
            <Section title="Standard Clauses" icon="gavel">
              <p className="text-xs text-gray-500 mb-1">Toggle clauses on/off and edit as needed.</p>
              {(Object.keys(clauses) as (keyof AgreementClauses)[]).map(key => (
                <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50">
                    <button
                      type="button"
                      onClick={() => setClause(key, 'enabled', !clauses[key].enabled)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${clauses[key].enabled ? 'bg-primary' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${clauses[key].enabled ? 'translate-x-4' : 'translate-x-1'}`} />
                    </button>
                    <span className="text-xs font-semibold text-gray-700">{CLAUSE_LABELS[key]}</span>
                  </div>
                  {clauses[key].enabled && (
                    <div className="p-3">
                      <textarea
                        value={clauses[key].content}
                        onChange={e => setClause(key, 'content', e.target.value)}
                        className={`${textareaCls} text-xs`}
                        rows={4}
                      />
                    </div>
                  )}
                </div>
              ))}
            </Section>

            {/* Internal notes */}
            <Section title="Internal Notes" icon="note">
              <Field label="Not shown on the agreement">
                <textarea
                  value={form.internal_notes}
                  onChange={e => set('internal_notes', e.target.value)}
                  placeholder="Internal notes for your records…"
                  className={textareaCls}
                  rows={3}
                />
              </Field>
            </Section>
          </div>
        </div>

        {/* Preview pane */}
        <div
          ref={printRef}
          className={`${tab === 'form' ? 'hidden' : 'flex'} lg:flex flex-col w-full lg:w-[45%] overflow-y-auto bg-gray-100`}
        >
          <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-4 py-2.5 bg-white border-b border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Live Preview</p>
            <button onClick={handlePrint} className="flex items-center gap-1 text-xs text-primary hover:underline font-medium">
              <span className="material-symbols-outlined text-sm">print</span>
              Print / Export PDF
            </button>
          </div>
          <div className="p-4">
            <div className="shadow-lg rounded-lg overflow-hidden border border-gray-200">
              <AgreementPreview form={form} agreementNumber={agreementNumber || 'AGR-XXXX-XXX'} />
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
